import React, {useState} from "react";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {translateCrmRequestError} from "../../../models/crm/crm-error";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";

const textValue = (value) => value === null || value === undefined ? "" : String(value);

function addressValue(address) {
    if (!address) return "";
    if (typeof address === "string") return address;
    return address.formatted || address.fullAddress || address.address || "";
}

export default function InlineCustomerDetails({customer, canEdit, onSave}) {
    const translate = useTranslate();
    const [activeField, setActiveField] = useState("");
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const definitions = [
        {field: "customerType", label: "crm.customer.type", icon: "mi mi:category", options: ["person", "company"]},
        {field: "status", label: "crm.customer.status", icon: "mi mi:toggle_on", options: ["active", "inactive"]},
        {field: "displayName", label: "crm.customer.name", icon: "mi mi:badge", required: true},
        {field: "firstName", label: "crm.customer.first-name", icon: "mi mi:person"},
        {field: "lastName", label: "crm.customer.last-name", icon: "mi mi:person"},
        {field: "companyName", label: "crm.customer.company-name", icon: "mi mi:domain"},
        {field: "jobTitle", label: "crm.customer.job-title", icon: "mi mi:work"},
        {field: "primaryPhone", label: "crm.customer.phone", icon: "mi mi:phone"},
        {field: "secondaryPhone", label: "crm.customer.secondary-phone", icon: "mi mi:phone_in_talk"},
        {field: "primaryEmail", label: "crm.customer.email", icon: "mi mi:mail"},
        {field: "secondaryEmail", label: "crm.customer.secondary-email", icon: "mi mi:alternate_email"},
        {field: "website", label: "crm.customer.website", icon: "mi mi:language"},
        {field: "externalId", label: "crm.customer.external-id", icon: "mi mi:fingerprint"},
        {field: "address", label: "crm.customer.address", icon: "mi mi:location_on"},
        {field: "description", label: "crm.customer.description", icon: "mi mi:notes"},
    ];

    const rawValue = (field) => field === "address" ? addressValue(customer?.address) : textValue(customer?.[field]);
    const displayValue = (definition) => {
        const value = rawValue(definition.field);
        return definition.options && value ? translate(`crm.customer.${value}`) : value || translate("crm.value.empty");
    };
    const beginEdit = (definition) => {
        if (!canEdit || saving) return;
        setActiveField(definition.field);
        setDraft(rawValue(definition.field));
        setError("");
    };
    const cancelEdit = () => {
        if (saving) return;
        setActiveField("");
        setDraft("");
        setError("");
    };
    const save = async (definition) => {
        const next = textValue(draft).trim();
        if (definition.required && !next) {
            setError(translate("crm.customer.validation.name"));
            return;
        }
        setSaving(true);
        setError("");
        try {
            await onSave(definition.field, definition.field === "address" ? (next ? {formatted: next} : null) : next);
            setActiveField("");
            setDraft("");
        } catch (requestError) {
            setError(translateCrmRequestError(translate, requestError, "crm.error.save"));
        } finally {
            setSaving(false);
        }
    };
    const keyDown = (event, definition) => {
        if (event.key === "Enter") {
            event.preventDefault();
            save(definition);
        }
        if (event.key === "Escape") cancelEdit();
    };

    return <div className="flex flex-column w-full min-w-0 overflow-hidden">
        {error && <Message severity="error" className="text-xs" text={error}/>} 
        {definitions.map((definition) => <div key={definition.field} className="flex align-items-start gap-2 py-1 w-full min-w-0 overflow-hidden">
            <Icon icon={`${definition.icon} text-500 text-base mt-1 flex-none`}/>
            <div className="flex-1 min-w-0 overflow-hidden flex flex-column">
                <span className="text-2xs text-500">{translate(definition.label)}</span>
                {activeField === definition.field ? <div className="flex align-items-center w-full min-w-0 overflow-hidden">
                    {definition.options ? <Dropdown value={draft} options={definition.options.map((value) => ({value, label: translate(`crm.customer.${value}`)}))} onChange={(event) => setDraft(event.value)} className="flex-1 min-w-0 border-none shadow-none text-xs" panelClassName="rtl text-xs" autoFocus pt={{root: {className: "border-none shadow-none min-w-0"}, input: {className: "p-0 text-xs"}, trigger: {className: "p-0 w-2rem"}}}/> : <InputText value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => keyDown(event, definition)} className="flex-1 min-w-0 border-none shadow-none p-0 text-xs" autoFocus/>}
                    <div className="flex flex-none align-items-center gap-1 mr-1">
                        <Icon icon={saving ? "mi mi:progress_activity" : "mi mi:check"} size={18} className="flex-none text-primary p-1" ariaLabel={translate("confirm")} disabled={saving} onClick={() => save(definition)}/>
                        <Icon icon="mi mi:close" size={18} className="flex-none text-500 p-1" ariaLabel={translate("cancel")} disabled={saving} onClick={cancelEdit}/>
                    </div>
                </div> : <span className="text-xs font-medium text-700 line-height-2 white-space-normal overflow-hidden" title={displayValue(definition)}>{displayValue(definition)}</span>}
            </div>
            {canEdit && activeField !== definition.field && <Icon icon="mi mi:edit" size={18} className="flex-none text-primary p-1" ariaLabel={`${translate("edit")} ${translate(definition.label)}`} onClick={() => beginEdit(definition)}/>} 
        </div>)}
    </div>;
}
